import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronLeft, CreditCard, Shield } from 'lucide-react';
import { authService } from '../../services/authService';

const includedFeatures = [
  'Unlimited players per team',
  'FA Four Corner Model assessments',
  'Progress tracking & reports',
  'Mobile & desktop access',
  'Email support',
];

export const ConfirmSubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData, selectedTier } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  if (!registrationData || !selectedTier) {
    navigate('/administration-club/register');
    return null;
  }

  const handleConfirm = async () => {
    setIsLoading(true);
    setServerError('');
    try {
      const response = await authService.registerWithPayment({
        ...registrationData,
        quantity: selectedTier.quantity,
        tierName: selectedTier.label,
      });
      window.location.href = response.checkoutUrl;
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to create checkout session');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/administration-club/select-tier', {
      state: { registrationData },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Side - Order Summary & Features */}
        <div className="hidden lg:block">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
              <span className="text-lg font-normal text-gray-500 ml-2">for Clubs</span>
            </h1>
            <p className="text-xl text-gray-600">
              You're almost there — review and confirm your subscription
            </p>
          </div>

          {/* Price Summary Card */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 border border-green-200 mb-8">
            <p className="text-sm text-gray-600 mb-1">{selectedTier.label}</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-bold text-gray-900">£{selectedTier.totalPrice}</span>
              <span className="text-gray-600">/month</span>
            </div>
            <p className="text-sm text-green-700 font-medium">
              £{selectedTier.pricePerTeam} per team • {selectedTier.quantity} teams included
            </p>
          </div>

          {/* What's Included */}
          <div className="space-y-3 mb-8">
            <h3 className="text-sm font-semibold text-gray-900">What's included</h3>
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">{selectedTier.quantity} teams</span>
            </div>
            {includedFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>

          {/* Security Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Payments are processed securely via Stripe. Your card details are never stored on our servers.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Confirmation Details */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 lg:p-10">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-3xl font-bold mb-1">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
            </h1>
            <p className="text-gray-600">Confirm your subscription</p>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Your Subscription</h2>
            <p className="text-gray-600">Review your details before proceeding to payment.</p>
          </div>

          {/* Mobile Price Summary */}
          <div className="lg:hidden bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-5 border border-green-200 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-1">{selectedTier.label}</p>
            <div className="flex items-baseline justify-center gap-2 mb-1">
              <span className="text-3xl font-bold text-gray-900">£{selectedTier.totalPrice}</span>
              <span className="text-gray-600">/month</span>
            </div>
            <p className="text-sm text-green-700 font-medium">
              £{selectedTier.pricePerTeam} per team
            </p>
          </div>

          {/* Account Information */}
          <div className="bg-gray-50 rounded-xl p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Account Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Name</span>
                <span className="text-gray-900 font-medium">{registrationData.firstName} {registrationData.lastName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Email</span>
                <span className="text-gray-900 font-medium">{registrationData.email}</span>
              </div>
            </div>
          </div>

          {/* Club Information */}
          <div className="bg-gray-50 rounded-xl p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Club Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Club Name</span>
                <span className="text-gray-900 font-medium">{registrationData.clubName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Address</span>
                <span className="text-gray-900 font-medium text-right max-w-[60%]">{registrationData.address}</span>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Subscription Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Number of Teams</span>
                <span className="text-gray-900 font-medium">{selectedTier.quantity} teams</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Price per Team</span>
                <span className="text-gray-900 font-medium">£{selectedTier.pricePerTeam}/month</span>
              </div>
              <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-900">Total Monthly Cost</span>
                <span className="text-lg font-bold text-green-600">£{selectedTier.totalPrice}/month</span>
              </div>
            </div>
          </div>

          {serverError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-600">{serverError}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleBack}
              disabled={isLoading}
              className="flex items-center justify-center gap-1 px-5 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CreditCard className="w-4 h-4" />
              {isLoading ? 'Processing...' : 'Proceed to Payment'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
            By proceeding, you agree to our Terms of Service and Privacy Policy.
            Your subscription will be charged monthly based on the number of teams selected.
            You can upgrade or downgrade at any time.
          </p>
        </div>

      </div>
    </div>
  );
};
